import React from 'react';
import { ConfigProvider, Segmented, Tooltip } from 'antd';
import { useTheme } from '../../../tokens';
import { setDisplayCurrency, useDisplayCurrency } from '../hooks/useDisplayCurrency';
import type { DisplayCurrency } from '../utils/format';

const EASE = '150ms cubic-bezier(0.23, 1, 0.32, 1)';

/**
 * DisplayCurrencyToggle — lente de moneda de los montos financieros.
 * Misma familia que ExchangeRateWidget (36px, borde, radio sm, punto de estado) para que
 * se lean como un solo instrumento: la tasa a la izquierda, en qué moneda leer a la derecha.
 * Bs es el estado nativo y queda en reposo; en moneda base el punto se enciende y el borde
 * sube a énfasis, para que nunca se confunda un monto convertido con uno liquidado.
 */
export const DisplayCurrencyToggle: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const { t } = useTheme();
  const value = useDisplayCurrency();
  const converted = value === 'BASE';

  return (
    <Tooltip
      placement="bottom"
      mouseEnterDelay={0.5}
      title={
        <div style={{ fontSize: t.fontCaption, lineHeight: 1.5, maxWidth: 280 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Moneda de los montos</div>
          <div style={{ opacity: 0.8 }}>Pagos y facturas usan la tasa registrada al generarse.</div>
          <div style={{ opacity: 0.8 }}>
            Comisiones (USD/EUR) pasan a Bs con la tasa del día y se marcan con ≈.
          </div>
        </div>
      }
    >
      <div
        role="group"
        aria-label="Moneda de los montos"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 36,
          padding: compact ? 3 : '0 3px 0 10px',
          borderRadius: t.radiusSm,
          background: t.surfaceInset,
          border: `1px solid ${converted ? t.borderEmphasis : t.border}`,
          transition: `border-color ${EASE}`,
        }}
      >
        {!compact && (
          <>
            <span
              aria-hidden
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                flexShrink: 0,
                background: converted ? t.primary : t.textMuted,
                transition: `background-color ${EASE}`,
              }}
            />
            <span
              style={{
                fontSize: t.fontCaption,
                fontWeight: 500,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: converted ? t.textSecondary : t.textMuted,
                whiteSpace: 'nowrap',
                transition: `color ${EASE}`,
              }}
            >
              Montos
            </span>
            <span aria-hidden style={{ width: 1, height: 14, background: t.border }} />
          </>
        )}
        <ConfigProvider
          theme={{
            components: {
              Segmented: {
                trackBg: 'transparent',
                trackPadding: 0,
                itemColor: t.textMuted,
                itemHoverColor: t.textPrimary,
                itemHoverBg: 'transparent',
                itemActiveBg: 'transparent',
                itemSelectedBg: t.surfaceElevated,
                itemSelectedColor: t.textPrimary,
                // Borders-only: el "thumb" se recorta con un anillo de 1px, no con sombra.
                boxShadowTertiary: `inset 0 0 0 1px ${t.borderEmphasis}`,
                // Concéntrico: radio del contenedor (sm) − padding (3px).
                borderRadiusSM: Math.max(t.radiusSm - 3, 2),
                controlHeightSM: 28,
                fontSizeSM: t.fontLabel,
                paddingXS: 10,
              },
            },
          }}
        >
          <Segmented<DisplayCurrency>
            size="small"
            value={value}
            onChange={setDisplayCurrency}
            options={[
              { label: 'Bs', value: 'VES', title: 'Bolívares' },
              { label: 'USD/EUR', value: 'BASE', title: 'Moneda base de cada aliado' },
            ]}
            style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', letterSpacing: '0.01em' }}
          />
        </ConfigProvider>
      </div>
    </Tooltip>
  );
};

export default DisplayCurrencyToggle;
